import { useEffect, useState, useMemo } from 'react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Buildings, MagnifyingGlass } from '@phosphor-icons/react';

const HEADER_ICON = <Buildings size={26} />;
import {
  fetchAdminSchoolDetail,
  fetchAdminSchoolDirectors,
  fetchAdminSchoolTeachers,
  fetchAdminSchoolClassrooms,
  fetchAdminSchoolStudents,
  resendDirectorInvite,
  updateSchoolDepartment,
  inviteUser,
  removeRole,
  unarchiveClassroom,
  suspendRole,
  reactivateRole,
  type SchoolDetailRow,
  type SchoolDirectorRow,
  type AdminSchoolTeacherRow,
  type SchoolClassroomRow,
  type SchoolStudentRow,
} from '../lib/admin-users';

const TAB_PAGE_SIZE = 10;
type Tab = 'teachers' | 'classrooms' | 'students';

export default function AdminUsersSchoolDetail() {
  const { id: schoolId } = useParams<{ id: string }>();

  const [school, setSchool] = useState<SchoolDetailRow | null>(null);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState<Tab>('teachers');
  const [search, setSearch] = useState('');
  const [tabPage, setTabPage] = useState(0);

  const [teachers, setTeachers] = useState<AdminSchoolTeacherRow[]>([]);
  const [teachersTotal, setTeachersTotal] = useState(0);
  const [classrooms, setClassrooms] = useState<SchoolClassroomRow[]>([]);
  const [classroomsTotal, setClassroomsTotal] = useState(0);
  const [students, setStudents] = useState<SchoolStudentRow[]>([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [tabLoading, setTabLoading] = useState(false);

  const [classFilter, setClassFilter] = useState('');
  const [showArchivedClassrooms, setShowArchivedClassrooms] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null);
  const [resendingDirectorId, setResendingDirectorId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deptEditing, setDeptEditing] = useState(false);
  const [deptValue, setDeptValue] = useState('');
  const [deptSaving, setDeptSaving] = useState(false);

  const [directors, setDirectors] = useState<SchoolDirectorRow[]>([]);
  const [addDirectorEmail, setAddDirectorEmail] = useState('');
  const [addDirectorBusy, setAddDirectorBusy] = useState(false);
  const [removingDirectorId, setRemovingDirectorId] = useState<string | null>(null);
  const [suspendingDirectorId, setSuspendingDirectorId] = useState<string | null>(null);

  const loadDirectors = (id: string) => {
    fetchAdminSchoolDirectors(id).then(setDirectors).catch(() => {});
  };

  useEffect(() => {
    if (schoolId) loadDirectors(schoolId);
  }, [schoolId]);

  useEffect(() => {
    fetchAdminSchoolDetail(schoolId)
      .then((s) => setSchool(s))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    setTabLoading(true);
    const load = async () => {
      if (tab === 'teachers') {
        const r = await fetchAdminSchoolTeachers(schoolId, tabPage, TAB_PAGE_SIZE);
        setTeachers(r.teachers); setTeachersTotal(r.total);
      } else if (tab === 'classrooms') {
        const r = await fetchAdminSchoolClassrooms(schoolId, tabPage, TAB_PAGE_SIZE, showArchivedClassrooms);
        setClassrooms(r.classrooms); setClassroomsTotal(r.total);
      } else {
        const r = await fetchAdminSchoolStudents(schoolId, tabPage, TAB_PAGE_SIZE);
        setStudents(r.students); setStudentsTotal(r.total);
      }
    };
    load().catch(() => {}).finally(() => setTabLoading(false));
  }, [tab, tabPage, schoolId, showArchivedClassrooms]);

  const onToggleShowArchivedClassrooms = (checked: boolean) => {
    setShowArchivedClassrooms(checked);
    setTabPage(0);
  };

  const onUnarchiveClassroom = async (classroomId: string) => {
    if (!schoolId) return;
    setUnarchivingId(classroomId);
    try {
      await unarchiveClassroom(classroomId);
      flash('Classe désarchivée.');
      const r = await fetchAdminSchoolClassrooms(schoolId, tabPage, TAB_PAGE_SIZE, showArchivedClassrooms);
      setClassrooms(r.classrooms); setClassroomsTotal(r.total);
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally {
      setUnarchivingId(null);
    }
  };

  const filteredTeachers = useMemo(() => {
    if (!search.trim()) return teachers;
    const q = search.toLowerCase();
    return teachers.filter((t) => `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) || (t.email?.toLowerCase().includes(q) ?? false));
  }, [teachers, search]);

  const filteredClassrooms = useMemo(() => {
    if (!search.trim()) return classrooms;
    const q = search.toLowerCase();
    return classrooms.filter((c) => c.name.toLowerCase().includes(q));
  }, [classrooms, search]);

  const filteredStudents = useMemo(() => {
    let list = students;
    if (classFilter) list = list.filter((s) => s.classroom_name === classFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.anon_id.toLowerCase().includes(q));
    }
    return list;
  }, [students, search, classFilter]);

  const classroomNames = useMemo(() => [...new Set(students.map((s) => s.classroom_name))].sort(), [students]);

  const currentTotal = tab === 'teachers' ? teachersTotal : tab === 'classrooms' ? classroomsTotal : studentsTotal;
  const currentList = tab === 'teachers' ? filteredTeachers : tab === 'classrooms' ? filteredClassrooms : filteredStudents;
  const totalPages = Math.max(1, Math.ceil(currentTotal / TAB_PAGE_SIZE));

  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(null), 4000); };

  const onEditDept = () => {
    setDeptValue(school?.department ?? '');
    setDeptEditing(true);
  };

  const onSaveDept = async () => {
    if (!schoolId) return;
    setDeptSaving(true);
    try {
      const dept = deptValue.trim() || null;
      await updateSchoolDepartment(schoolId, dept);
      setSchool((s) => s ? { ...s, department: dept } : s);
      setDeptEditing(false);
      flash('Département mis à jour.');
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally {
      setDeptSaving(false);
    }
  };

  const onResend = async (director: SchoolDirectorRow) => {
    if (!schoolId || !director.email) return;
    setResendingDirectorId(director.id);
    try {
      await resendDirectorInvite(schoolId, director.user_id, director.email);
      flash(`Invitation renvoyée à ${director.email}.`);
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setResendingDirectorId(null); }
  };

  const onAddDirector = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schoolId || !addDirectorEmail.trim()) return;
    setAddDirectorBusy(true);
    try {
      const res = await inviteUser(addDirectorEmail.trim(), 'direction', 'school', schoolId);
      flash(
        res.status === 'assigned'
          ? `${addDirectorEmail.trim()} a déjà un compte — rôle direction assigné directement.`
          : `Invitation envoyée à ${addDirectorEmail.trim()}.`,
      );
      setAddDirectorEmail('');
      if (schoolId) loadDirectors(schoolId);
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setAddDirectorBusy(false); }
  };

  const onRemoveDirector = async (id: string) => {
    setRemovingDirectorId(id);
    try {
      await removeRole(id);
      flash('Directeur retiré.');
      if (schoolId) loadDirectors(schoolId);
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setRemovingDirectorId(null); }
  };

  const onToggleSuspendDirector = async (d: SchoolDirectorRow) => {
    setSuspendingDirectorId(d.id);
    try {
      if (d.suspended_at) {
        await reactivateRole(d.id);
        flash('Directeur réactivé.');
      } else {
        await suspendRole(d.id);
        flash('Directeur suspendu.');
      }
      if (schoolId) loadDirectors(schoolId);
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setSuspendingDirectorId(null); }
  };

  const onSearch = (v: string) => { setSearch(v); setTabPage(0); };
  const onClassFilter = (v: string) => { setClassFilter(v); setTabPage(0); };

  const headerBack = { backTo: '/admin/users/schools', backLabel: 'Établissements', icon: HEADER_ICON } as const;

  if (loading) return <><HeaderContent width="md" showBurger {...headerBack}>{null}</HeaderContent><main className="max-w-[920px] mx-auto px-4 pt-8"><p className="text-[var(--naia-text-secondary)]">Chargement…</p></main></>;
  if (!school) return <><HeaderContent width="md" showBurger {...headerBack}>{null}</HeaderContent><main className="max-w-[920px] mx-auto px-4 pt-8"><p className="text-[var(--naia-text-secondary)]">Établissement introuvable.</p></main></>;

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'teachers', label: 'Enseignants', count: teachersTotal },
    { key: 'classrooms', label: 'Classes', count: classroomsTotal },
    { key: 'students', label: 'Élèves', count: studentsTotal },
  ];

  return (
    <>
      <HeaderContent width="md" showBurger {...headerBack}>
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>{school.name}</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Créé le {new Date(school.created_at).toLocaleDateString('fr-FR')}
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[920px] mx-auto px-4 pb-16 pt-4">

        {notice && (
          <p className="mb-4 px-4 py-3 rounded-[10px] bg-naia-sky text-naia-blue text-sm font-semibold">{notice}</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Enseignants', value: school.teacher_count },
            { label: 'Classes', value: school.class_count },
            { label: 'Élèves actifs', value: school.active_student_count },
            { label: 'Total élèves', value: school.student_count },
          ].map(({ label, value }) => (
            <div key={label} className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] px-4 py-3 flex flex-col gap-1">
              <span className="text-[10.5px] font-black tracking-[0.8px] uppercase text-[var(--naia-text-secondary)]">{label}</span>
              <span className="text-[28px] font-extrabold text-[var(--naia-text-primary)] leading-none tabular-nums">{value}</span>
            </div>
          ))}
        </div>

        <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] px-5 py-4 mb-3">
          <h2 className="text-[10.5px] font-black tracking-[0.8px] uppercase text-[var(--naia-text-secondary)] mb-3 mt-0">Département</h2>
          {deptEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={deptValue}
                onChange={(e) => setDeptValue(e.target.value)}
                placeholder="ex: 75"
                maxLength={3}
                className="w-24 text-[13px]"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') void onSaveDept(); if (e.key === 'Escape') setDeptEditing(false); }}
              />
              <Button size="sm" onClick={() => void onSaveDept()} disabled={deptSaving}>{deptSaving ? 'Enregistrement…' : 'Enregistrer'}</Button>
              <Button size="sm" variant="outline" onClick={() => setDeptEditing(false)}>Annuler</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-[15px] font-semibold text-[var(--naia-text-primary)]">
                {school.department ?? <span className="text-[var(--naia-text-secondary)] opacity-50">—</span>}
              </span>
              <button onClick={onEditDept} className="text-[11.5px] font-bold text-naia-blue hover:underline">
                Modifier
              </button>
            </div>
          )}
        </div>

        <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] px-5 py-4 mb-6">
          <h2 className="text-[10.5px] font-black tracking-[0.8px] uppercase text-[var(--naia-text-secondary)] mb-1 mt-0">Direction — accès complet</h2>
          <p className="text-[11.5px] text-[var(--naia-text-secondary)] mb-3 mt-0">
            Comptes avec pleins pouvoirs direction sur cet établissement. Distinct de la délégation par
            groupe de classes (sous-direction), gérée depuis l'espace direction de l'école.
          </p>

          {directors.length > 0 ? (
            <ul className="flex flex-col gap-2 mb-3">
              {directors.map((d) => (
                <li key={d.id} className="flex items-center justify-between flex-wrap gap-3 px-3 py-2 rounded-[8px] bg-[var(--naia-card-alt,rgba(0,0,0,0.02))]">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${d.suspended_at ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                    <span className="text-[13.5px] font-semibold text-[var(--naia-text-primary)]">{d.email}</span>
                    {d.suspended_at && (
                      <span className="text-[10.5px] font-bold text-[var(--naia-text-secondary)] uppercase">Suspendu</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => void onResend(d)}
                      disabled={resendingDirectorId === d.id || !d.email}
                      className="text-[11.5px] font-bold text-naia-blue hover:underline disabled:opacity-50"
                    >
                      {resendingDirectorId === d.id ? 'Envoi…' : 'Renvoyer invitation'}
                    </button>
                    <button
                      onClick={() => void onToggleSuspendDirector(d)}
                      disabled={suspendingDirectorId === d.id}
                      className="text-[11.5px] font-bold text-naia-blue hover:underline disabled:opacity-50"
                    >
                      {suspendingDirectorId === d.id ? '…' : d.suspended_at ? 'Réactiver' : 'Suspendre'}
                    </button>
                    <button
                      onClick={() => void onRemoveDirector(d.id)}
                      disabled={removingDirectorId === d.id}
                      className="text-[11.5px] font-bold text-red-600 hover:underline disabled:opacity-50"
                    >
                      {removingDirectorId === d.id ? 'Retrait…' : 'Retirer'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-[var(--naia-text-secondary)] mb-3">Aucun directeur assigné.</p>
          )}

          <form onSubmit={onAddDirector} className="flex items-center gap-2 flex-wrap">
            <Input
              type="email"
              value={addDirectorEmail}
              onChange={(e) => setAddDirectorEmail(e.target.value)}
              placeholder="nouveau-directeur@exemple.fr"
              className="flex-1 min-w-[220px] text-[12.5px]"
              required
            />
            <Button type="submit" size="sm" disabled={addDirectorBusy}>
              {addDirectorBusy ? '…' : 'Ajouter un directeur'}
            </Button>
          </form>
        </div>

        <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden">
          <div className="flex border-b border-[var(--naia-line)]">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setSearch(''); setTabPage(0); setClassFilter(''); }}
                className={`px-5 py-3 text-[12.5px] font-bold transition-colors border-b-2 -mb-px ${
                  tab === t.key
                    ? 'border-naia-blue text-naia-blue'
                    : 'border-transparent text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-extrabold ${tab === t.key ? 'bg-naia-blue text-white' : 'bg-[var(--naia-line)] text-[var(--naia-text-secondary)]'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-4 border-b border-[var(--naia-line)] flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]" />
              <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={`Rechercher…`} className="pl-8 text-[12.5px]" />
            </div>
            {tab === 'classrooms' && (
              <label className="flex items-center gap-2 text-[12px] font-semibold text-[var(--naia-text-secondary)] whitespace-nowrap px-1">
                <input
                  type="checkbox"
                  checked={showArchivedClassrooms}
                  onChange={(e) => onToggleShowArchivedClassrooms(e.target.checked)}
                />
                Afficher les classes archivées
              </label>
            )}
            {tab === 'students' && classroomNames.length > 0 && (
              <select
                value={classFilter}
                onChange={(e) => onClassFilter(e.target.value)}
                className="border border-[var(--naia-line)] rounded-[8px] px-3 text-[12.5px] bg-[var(--naia-card)] text-[var(--naia-text-primary)] min-w-[140px]"
              >
                <option value="">Toutes les classes</option>
                {classroomNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
          </div>

          <div className="min-h-[220px]">
            {tabLoading ? (
              <p className="text-[var(--naia-text-secondary)] text-[13px] px-5 py-6">Chargement…</p>
            ) : currentList.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] text-[13px] px-5 py-6">Aucun résultat.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[12.5px]">
                  <thead>
                    <tr className="border-b border-[var(--naia-line)] bg-[var(--naia-card-alt,var(--naia-card))]">
                      {tab === 'teachers' && ['Nom', 'Email', 'Statut', 'Ajouté le'].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 font-black text-[10px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase whitespace-nowrap">{h}</th>
                      ))}
                      {tab === 'classrooms' && [
                        'Classe', 'Niveau', 'Prof principal', 'Élèves actifs / Total',
                        ...(showArchivedClassrooms ? ['Actions'] : []),
                      ].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 font-black text-[10px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase whitespace-nowrap">{h}</th>
                      ))}
                      {tab === 'students' && ['Identifiant', 'Classe', 'Statut'].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 font-black text-[10px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--naia-line)]">
                    {tab === 'teachers' && filteredTeachers.map((t) => (
                      <tr key={t.id} className="hover:bg-[var(--naia-card-alt,rgba(0,0,0,0.02))]">
                        <td className="px-4 py-2.5 font-semibold text-[var(--naia-text-primary)]">{t.first_name} {t.last_name}</td>
                        <td className="px-4 py-2.5 text-[var(--naia-text-secondary)]">{t.email ?? <span className="opacity-30">—</span>}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${t.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {t.active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[var(--naia-text-secondary)] whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))}
                    {tab === 'classrooms' && filteredClassrooms.map((c) => (
                      <tr key={c.id} className="hover:bg-[var(--naia-card-alt,rgba(0,0,0,0.02))]">
                        <td className="px-4 py-2.5 font-semibold text-[var(--naia-text-primary)]">
                          {c.name}
                          {c.archived && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-gray-100 text-gray-500 align-middle">Archivée</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-[var(--naia-text-secondary)]">{c.level ?? <span className="opacity-30">—</span>}</td>
                        <td className="px-4 py-2.5 text-[var(--naia-text-secondary)]">{c.primary_teacher_name ?? <span className="opacity-30">—</span>}</td>
                        <td className="px-4 py-2.5 tabular-nums">
                          <span className="font-extrabold text-[var(--naia-text-primary)]">{c.active_count}</span>
                          <span className="text-[var(--naia-text-secondary)]"> / {c.total_count}</span>
                        </td>
                        {showArchivedClassrooms && (
                          <td className="px-4 py-2.5">
                            {c.archived && (
                              <button
                                onClick={() => void onUnarchiveClassroom(c.id)}
                                disabled={unarchivingId === c.id}
                                className="text-[11.5px] font-bold text-naia-blue hover:underline disabled:opacity-50"
                              >
                                {unarchivingId === c.id ? 'Désarchivage…' : 'Désarchiver'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {tab === 'students' && filteredStudents.map((s) => (
                      <tr key={s.id} className="hover:bg-[var(--naia-card-alt,rgba(0,0,0,0.02))]">
                        <td className="px-4 py-2.5 font-mono font-semibold text-[var(--naia-text-primary)] tracking-wide">{s.anon_id}</td>
                        <td className="px-4 py-2.5 text-[var(--naia-text-secondary)]">{s.classroom_name}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${s.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {s.active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && !tabLoading && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--naia-line)] text-[12px]">
              <span className="text-[var(--naia-text-secondary)]">
                Page {tabPage + 1} / {totalPages} — {currentTotal} au total
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setTabPage((p) => Math.max(0, p - 1))} disabled={tabPage === 0}>Précédent</Button>
                <Button variant="outline" size="sm" onClick={() => setTabPage((p) => Math.min(totalPages - 1, p + 1))} disabled={tabPage >= totalPages - 1}>Suivant</Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
